import React, { PropsWithChildren, useContext, useState } from 'react';
import SvgMenuDashboard from '@icons/grid.svg?react';
import SvgMenuSettings from '@icons/cog.svg?react';
import SvgMenuUser from '../icons/menu/MenuUser.tsx';
import SvgMenuTms from '@icons/truck-inverted.svg?react';
import SvgMenuTimeslot from '@icons/calendar-inverted.svg?react';
import SvgMenuSn from '@icons/megaphone.svg?react';
import SvgMenuContacts from '@icons/git-fork.svg?react';
import SvgMenuReports from '@icons/chart-bar.svg?react';
import SvgMenuActivities from '@icons/book-menu.svg?react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { CompanyModuleEnum, FeaturesEnum, PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import { isNullOrUndefined, isProduction, useLocalStorage } from '@utils/utils.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import PermissionError from '@components/ringil3/PermissionError/PermissionError.tsx';
import AppSidebar from '@app/layout/AppSidebar.tsx';
import useSidebarState from '@app/layout/useSidebarState.tsx';
import { MenuItemModel } from '@components/menu/types.ts';
import MenuItem from '@components/menu/MenuItem.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { useLanguageMenuItem } from '@hooks/i18n/useLanguageMenuItem.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { useJobBadgeCounts } from '@hooks/useJobs/useJobBadgeCounts.ts';
import { useAuth0 } from '@auth0/auth0-react';
import { Note } from '@components/styles.tsx';
import MaintenanceBanner from '@components/appMessages/MaintenanceBanner.tsx';
import NotificationMenuItem from '@app/layout/NotificationMenuItem.tsx';
import CustomerNotificationMenuItem from '@app/layout/CustomerNotificationMenuItem.tsx';
import Customer2InheritorNotificationSidebar from '@components/ringil3/Features/Customer2InheritorNotification/sidebars/Customer2InheritorNotificationSidebar.tsx';
import { ApiContext } from '@api/api.tsx';

import { useSearchParams } from 'react-router-dom';
import useActiveRoute from '@hooks/useActiveRoute/useActiveRoute.tsx';
import SecurityModeMenuSwitch from '@components/security/SecurityModeMenuSwitch.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag.ts';
import { useAssistant } from '@components/ringil3/Features/Assistant/assistantContext.ts';
import SvgAiAssistant from '@icons/ai-robot.svg?react';
import useLockDocumentScroll from '@hooks/useLockDocumentScroll/useLockDocumentScroll.tsx';
import useShellMetrics from '@hooks/useShellMetrics/useShellMetrics.tsx';
import useBetaVersion from '@hooks/useBetaVersion/useBetaVersion.tsx';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces.ts';
import { DefaultVersionEnum } from '@api/ringil-api.v2';
import PageLayoutTiles from '@components/features/page-layout/PageLayoutTiles';
import { CONTENT_SCROLL_ATTR } from '@hooks/useScrollBehindPanel/useScrollBehindPanel.tsx';
import AppWatermark from '@app/layout/AppWatermark.tsx';
import { useImprovedLayout } from '@app/layout/improvedLayoutStore.ts';
import { useCreateTimeslotSidebar } from '@components/ringil3/Features/Reservation/Sidedbars/CreateTimeslotSidebar.tsx';

interface ComponentProps {
  title?: string;
  oneOfPermission?: PermissionTypesEnum[];
  layout?: 'calendar' | 'detail';
}

const ResponsiveLayout: React.FC<PropsWithChildren<ComponentProps>> = ({ title, layout, oneOfPermission, children }) => {
  // The shell below is exactly one viewport tall and scrolls internally — the document must never scroll too.
  useLockDocumentScroll();
  const sidebar = useSidebarState();
  const isMobile = sidebar.isMobile;

  const { contentRef, restWidth } = useShellMetrics();
  // Off by default: the content then spans the full width of the shell, as it did before the rework.
  const improvedLayout = useImprovedLayout();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const { logout } = useAuth0();
  const languageMenuItem = useLanguageMenuItem();
  const { listPowerBIReports } = useContext(ApiContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { isRouteActive, isHomeActive } = useActiveRoute();
  const runAs = searchParams.get('runAs');

  const { loggedUser, hasModule, hasPermission, isAdmin } = useContext(AuthenticatedUserContext);
  const [visible, setVisible] = useState(false);
  // Reachable from any page via the Timeslots > New menu entry, so the sidebar is mounted by the
  // layout rather than by a single page.
  const { CreateTimeslotSidebar, createTimeslotRequest, setCreateTimeslotRequest } = useCreateTimeslotSidebar();
  // The panel itself is mounted by AssistantProvider, above the router — this layout only triggers it.
  const { flag: assistantFlag, open: openAssistant } = useAssistant();

  // --- Menu security flags --------------------------------------------------------------------
  // Each menu item declares its visibility once as a SecuredFlag and attaches it via the item's
  // `secured` config, so the gate is inspectable in security mode instead of an inline boolean.
  // Declared here (before the early returns below) to keep the hook call order stable across renders.

  // Top-level groups
  const expeditionsCarrierFlag = useSecuredFlag('menu-expeditions-carrier', {
    kind: 'visibility',
    description: 'Carrier Expeditions menu',
    modules: [{ module: CompanyModuleEnum.CARRIER }],
    custom: [{
        label: 'not RESTRICT_NO_EXP',
        satisfied: !hasPermission([PermissionTypesEnum.RESTRICT_NO_EXP]),
        description: 'Hidden for users given the RESTRICT_NO_EXP restriction, which takes shipments away from them',
        byDesign: false,
      }],
  });
  const expeditionsCustomerFlag = useSecuredFlag('menu-expeditions', {
    kind: 'visibility',
    description: 'Customer Expeditions menu',
    custom: [
      {
        label: '(EXPEDITION & not RESTRICT_NO_EXP) or inherited CUSTOMS_DECLARATION',
        description:
          'Shown when the company has the Expedition module and the user has no RESTRICT_NO_EXP restriction, or when the company has the Customs declaration module from a partner',
        byDesign: false,
        satisfied:
          (hasModule(CompanyModuleEnum.EXPEDITION, null) && !hasPermission([PermissionTypesEnum.RESTRICT_NO_EXP])) ||
          hasModule(CompanyModuleEnum.CUSTOMS_DECLARATION, true),
      },
    ],
  });
  const timeslotsFlag = useSecuredFlag('menu-timeslots', {
    kind: 'visibility',
    description: 'Timeslots menu',
    modules: [{ module: CompanyModuleEnum.TIMESLOTS }],
    custom: [{
        label: 'not RESTRICT_NO_EXP_NO_TIMESLOT',
        satisfied: !hasPermission([PermissionTypesEnum.RESTRICT_NO_EXP_NO_TIMESLOT]),
        description: 'Hidden for users given the RESTRICT_NO_EXP_NO_TIMESLOT restriction, which takes shipments and timeslots away from them',
        byDesign: false,
      }],
  });
  const supplierNotificationFlag = useSecuredFlag('menu-supplier-notifications', {
    kind: 'visibility',
    description: 'Supplier notifications menu',
    modules: [{ module: CompanyModuleEnum.SUPPLIER_NOTIFICATION }, { module: CompanyModuleEnum.SHIPMENT }],
    custom: [{
        label: 'not RESTRICT_NO_SUPPNOTIFICATION',
        satisfied: !hasPermission([PermissionTypesEnum.RESTRICT_NO_SUPPNOTIFICATION]),
        description: 'Hidden for users given the RESTRICT_NO_SUPPNOTIFICATION restriction, which takes supplier notifications away from them',
        byDesign: false,
      }],
  });
  const reportsFlag = useSecuredFlag('menu-reports', {
    kind: 'visibility',
    description: 'PowerBI reports menu',
    data: [{ label: 'user has reports', satisfied: loggedUser?.reports?.length > 0 }],
  });
  const emailHistoryFlag = useSecuredFlag('menu-activities-email-history', {
    kind: 'visibility',
    description: 'Communication history',
    features: [FeaturesEnum.ENABLE_SENDER_EMAIL_HISTORY],
    permissions: [PermissionTypesEnum.PERMISSION_EMAIL_HISTORY_READ, PermissionTypesEnum.PERMISSION_EMAIL_HISTORY_RW],
  });
  const jobsFlag = useSecuredFlag('menu-jobs', {
    kind: 'visibility',
    description: 'Jobs center menu',
    permissions: [PermissionTypesEnum.PERMISSION_JOBS_READ, PermissionTypesEnum.PERMISSION_JOBS_RW],
  });
  const jobBadges = useJobBadgeCounts(jobsFlag.granted);
  const contactsFlag = useSecuredFlag('menu-contacts', {
    kind: 'visibility',
    description: 'Contacts menu',
    modules: [{ module: CompanyModuleEnum.EXPEDITION }],
    permissions: [
      PermissionTypesEnum.PERMISSION_CARRIER_GROUP_RW,
      PermissionTypesEnum.PERMISSION_CARRIER_GROUP_READ,
      PermissionTypesEnum.PERMISSION_MY_CARRIERS_RW,
      PermissionTypesEnum.PERMISSION_MY_CARRIERS_READ,
      PermissionTypesEnum.PERMISSION_CARRIER_CATALOGUE,
    ],
    custom: [{
        label: 'not RESTRICT_NO_EXP',
        satisfied: !hasPermission([PermissionTypesEnum.RESTRICT_NO_EXP]),
        description: 'Hidden for users given the RESTRICT_NO_EXP restriction, which takes shipments away from them',
        byDesign: false,
      }],
  });
  const settingsFlag = useSecuredFlag('menu-settings', {
    kind: 'visibility',
    description: 'Settings menu',
    custom: [{
        label: 'not RESTRICT_NO_SETTINGS',
        satisfied: !hasPermission([PermissionTypesEnum.RESTRICT_NO_SETTINGS]),
        description: 'Hidden for users given the RESTRICT_NO_SETTINGS restriction, which takes the settings away from them',
        byDesign: false,
      }],
  });

  // Customer Expeditions sub-items
  const expeditionCreateFlag = useSecuredFlag('menu-expeditions-create', {
    kind: 'visibility',
    description: 'Create expedition (v2)',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE],
  });
  const expeditionListFlag = useSecuredFlag('menu-expeditions-list', {
    kind: 'visibility',
    description: 'Expedition list',
    permissions: [
      PermissionTypesEnum.PERMISSION_EXPEDITION_LIST_READ,
      PermissionTypesEnum.PERMISSION_EXPEDITION_LIST_RW,
      PermissionTypesEnum.PERMISSION_EXPEDITION_LIST_READ_WITHOUTPRICE,
    ],
  });
  const expeditionTemplatesFlag = useSecuredFlag('menu-expeditions-templates', {
    kind: 'visibility',
    description: 'Expedition templates',
    modules: [{ module: CompanyModuleEnum.EXPEDITION, inherited: false }],
    features: [FeaturesEnum.TEMPLATES],
  });

  // Timeslots sub-items
  const timeslotsCalendarFlag = useSecuredFlag('menu-timeslots-calendar', {
    kind: 'visibility',
    description: 'Timeslot calendar',
    // Owned TIMESLOTS module (customer) OR inherited TIMESLOTS module (carrier/partner, carrier-side calendar).
    modules: [{ module: CompanyModuleEnum.TIMESLOTS }],
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_DASHBOARD_RW, PermissionTypesEnum.PERMISSION_TIMESLOT_DASHBOARD_READ],
  });
  const timeslotCreateFlag = useSecuredFlag('menu-timeslots-new', {
    kind: 'visibility',
    description: 'Create a reservation from the menu',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_LIST_RW],
  });
  const timeslotsReservationListFlag = useSecuredFlag('menu-timeslots-reservation-list', {
    kind: 'visibility',
    description: 'Reservation list',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_LIST_RW, PermissionTypesEnum.PERMISSION_TIMESLOT_LIST_READ],
  });
  const camsFlag = useSecuredFlag('menu-timeslots-cams', {
    kind: 'visibility',
    description: 'Cams',
    features: [FeaturesEnum.ENTRY_CAMS],
    permissions: [PermissionTypesEnum.PERMISSION_CAM_ENTRYLOG],
  });
  const camsSettingsFlag = useSecuredFlag('menu-timeslots-cams-settings', {
    kind: 'visibility',
    description: 'Cams settings',
    features: [FeaturesEnum.ENTRY_CAMS],
    permissions: [PermissionTypesEnum.PERMISSION_CAM_SETTINGS],
  });
  const visitorsFlag = useSecuredFlag('menu-timeslots-visitors', {
    kind: 'visibility',
    description: 'Visitors',
    features: [FeaturesEnum.VISITOR_RECORDS],
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_VISITORS_READ, PermissionTypesEnum.PERMISSION_TIMESLOT_VISITORS_WRITE],
  });

  // Supplier notifications sub-items
  const snNewFlag = useSecuredFlag('menu-sn-new', {
    kind: 'visibility',
    description: 'New supplier notification',
    modules: [{ module: CompanyModuleEnum.SUPPLIER_NOTIFICATION }],
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_CREATE],
  });
  const snNewDapFlag = useSecuredFlag('menu-sn-new-dap', {
    kind: 'visibility',
    description: 'New DAP',
    features: [FeaturesEnum.DAP],
    permissions: [PermissionTypesEnum.PERMISSION_SN_DXX_CREATE],
  });
  const snListFlag = useSecuredFlag('menu-sn-list', {
    kind: 'visibility',
    description: 'Supplier notification list',
    modules: [{ module: CompanyModuleEnum.SUPPLIER_NOTIFICATION }],
    permissions: [PermissionTypesEnum.PERMISSION_SN_LIST_READ, PermissionTypesEnum.PERMISSION_SN_LIST_RW],
  });
  const snGroupDemandsFlag = useSecuredFlag('menu-sn-group-demands', {
    kind: 'visibility',
    description: 'Group demands',
    modules: [{ module: CompanyModuleEnum.SUPPLIER_NOTIFICATION, inherited: false }],
  });
  const snShipmentsFlag = useSecuredFlag('menu-sn-shipments', {
    kind: 'visibility',
    description: 'Agent notification (shipments)',
    modules: [{ module: CompanyModuleEnum.SHIPMENT }],
  });

  // Contacts sub-items
  const myCarriersFlag = useSecuredFlag('menu-contacts-my-carriers', {
    kind: 'visibility',
    description: 'My carriers',
    permissions: [PermissionTypesEnum.PERMISSION_MY_CARRIERS_RW, PermissionTypesEnum.PERMISSION_MY_CARRIERS_READ],
  });
  const carrierGroupsFlag = useSecuredFlag('menu-contacts-carrier-groups', {
    kind: 'visibility',
    description: 'Carrier groups',
    permissions: [PermissionTypesEnum.PERMISSION_CARRIER_GROUP_RW, PermissionTypesEnum.PERMISSION_CARRIER_GROUP_READ],
  });
  const carrierCatalogFlag = useSecuredFlag('menu-contacts-carrier-catalog', {
    kind: 'visibility',
    description: 'Carrier catalog',
    permissions: [PermissionTypesEnum.PERMISSION_CARRIER_CATALOGUE],
  });
  const contactPartnersFlag = useSecuredFlag('menu-contacts-partners', {
    kind: 'visibility',
    description: 'Partners (contacts)',
    permissions: [PermissionTypesEnum.PERMISSION_PARTNERS],
  });

  // Settings sub-items
  const locationsFlag = useSecuredFlag('menu-settings-locations', {
    kind: 'visibility',
    description: 'Locations',
    permissions: [PermissionTypesEnum.PERMISSION_LOCATIONS],
  });
  const companyProfileFlag = useSecuredFlag('menu-settings-company-profile', {
    kind: 'visibility',
    description: 'Company profile',
    permissions: [PermissionTypesEnum.PERMISSION_SETTINGS_COMPANY_PROFILE],
  });
  const inheritedCompaniesFlag = useSecuredFlag('menu-settings-inherited-companies', {
    kind: 'visibility',
    description: 'Inherited companies',
    permissions: [PermissionTypesEnum.PERMISSION_SETTINGS_INHERITED_COMPANIES_READ],
  });
  const settingsPartnersFlag = useSecuredFlag('menu-settings-partners', {
    kind: 'visibility',
    description: 'Partners (settings)',
    permissions: [PermissionTypesEnum.PERMISSION_SETTINGS_PARTNERS_READ, PermissionTypesEnum.PERMISSION_SETTINGS_PARTNERS_RW],
  });
  const companyNotificationsFlag = useSecuredFlag('menu-settings-company-notifications', {
    kind: 'visibility',
    description: 'Company notifications',
    permissions: [PermissionTypesEnum.PERMISSION_SETTINGS_COMPANY_NOTIFICATIONS],
  });
  const exceptionsFlag = useSecuredFlag('menu-settings-exceptions', {
    kind: 'visibility',
    description: 'Exceptions',
    permissions: [PermissionTypesEnum.PERMISSION_EXCEPTIONS],
  });
  const volumetricFlag = useSecuredFlag('menu-settings-volumetric', {
    kind: 'visibility',
    description: 'Volumetric',
    permissions: [PermissionTypesEnum.PERMISSION_SHIPMENT_MANUALIMPORT],
  });
  const calendarLayoutFlag = useSecuredFlag('menu-settings-calendar-layout', {
    kind: 'visibility',
    description: 'Calendar card layouts',
    permissions: [PermissionTypesEnum.PERMISSION_CALENDAR_LAYOUT_READ, PermissionTypesEnum.PERMISSION_CALENDAR_LAYOUT_RW],
  });
  const aiDispatcherFlag = useSecuredFlag('menu-settings-ai-dispatcher', {
    kind: 'visibility',
    description: 'AI dispatcher rules',
    permissions: [PermissionTypesEnum.PERMISSION_AI_DISPATCHER_READ, PermissionTypesEnum.PERMISSION_AI_DISPATCHER_RW],
    features: [FeaturesEnum.AI_DISPATCHER],
  });
  const aiDispatcherActivityFlag = useSecuredFlag('menu-activities-ai-dispatcher', {
    kind: 'visibility',
    description: 'AI dispatcher activity',
    permissions: [PermissionTypesEnum.PERMISSION_AI_DISPATCHER_READ, PermissionTypesEnum.PERMISSION_AI_DISPATCHER_RW],
    features: [FeaturesEnum.AI_DISPATCHER],
  });
  /**
   * A section header is visible when anything inside it is. Spelling the permissions of one child
   * out here — as this did for the email history — hides the whole section from a user who has
   * only the other one, which is now a real case.
   */
  const activitiesFlag = useSecuredFlag('menu-activities', {
    kind: 'visibility',
    description: 'Activities menu',
    custom: [
      {
        label: 'at least one activities screen is visible',
        satisfied: emailHistoryFlag.granted || jobsFlag.granted || aiDispatcherActivityFlag.granted,
        description: 'Shown when the user can open at least one activities screen: communication history, jobs center or AI dispatcher activity',
        byDesign: false,
      },
    ],
  });

  // Function to get mobile menu items
  const getMobileMenuItems = () => {
    const items = [];

    // Dashboard
    items.push({
      icon: isHomeActive() ? <SvgMenuDashboard className={'text-content-brand'} /> : <SvgMenuDashboard />,
      label: tr('Layout.dashboard', 'Dashboard'),
      onClick: () => nav.navigate(nav.urlFunctions.createHomePage()),
      show: true,
      isActive: isHomeActive(),
    });

    // AI assistant — opens the sidebar instead of navigating, so it never takes the active state.
    items.push({
      icon: <SvgAiAssistant className={'w-5 h-5'} />,
      label: tr('Layout.aiAssistant', 'Poradím vám'),
      onClick: () => openAssistant(),
      show: assistantFlag.granted,
      isActive: false,
    });

    // Expeditions
    if (hasModule(CompanyModuleEnum.CARRIER, null)) {
      items.push({
        icon: isRouteActive('/carrier/shipments') || isRouteActive('/carrier/demands') ? <SvgMenuTms className={'text-content-brand'} /> : <SvgMenuTms />,
        label: tr('Layout.expeditions', 'Expeditions'),
        onClick: () => nav.navigate(nav.urlFunctions.createCarrierShipmentsList()),
        show: !hasPermission([PermissionTypesEnum.RESTRICT_NO_EXP]),
        isActive: isRouteActive('/carrier/shipments') || isRouteActive('/carrier/demands'),
      });
    } else if (hasModule(CompanyModuleEnum.EXPEDITION, null)) {
      items.push({
        icon: isRouteActive('/customer/expeditions') || isRouteActive('/customer/expedition') ? <SvgMenuTms className={'text-content-brand'} /> : <SvgMenuTms />,
        label: tr('Layout.expeditions', 'Expeditions'),
        onClick: () => nav.navigate(nav.urlFunctions.createCustomerExpeditionList()),
        show: !hasPermission([PermissionTypesEnum.RESTRICT_NO_EXP]),
        isActive: isRouteActive('/customer/expeditions') || isRouteActive('/customer/expedition'),
      });
    }

    // Timeslots
    // @TODO: hasModule(CompanyModuleEnum.TIMESLOTS, null): null -> false?
    if (
      hasModule(CompanyModuleEnum.TIMESLOTS, null) &&
      hasPermission([PermissionTypesEnum.PERMISSION_TIMESLOT_DASHBOARD_RW, PermissionTypesEnum.PERMISSION_TIMESLOT_DASHBOARD_READ])
    ) {
      items.push({
        icon: isRouteActive('/timeslots') ? <SvgMenuTimeslot className={'text-content-brand'} /> : <SvgMenuTimeslot />,
        label: tr('Layout.timeslots', 'Timeslots'),
        onClick: () => nav.navigate(nav.urlFunctions.createReservationList()),
        show: !hasPermission([PermissionTypesEnum.RESTRICT_NO_EXP_NO_TIMESLOT]),
        isActive: isRouteActive('/timeslots'),
      });
    }

    // Supplier Notifications
    if (hasModule(CompanyModuleEnum.SUPPLIER_NOTIFICATION, null) || hasModule(CompanyModuleEnum.SHIPMENT, null)) {
      items.push({
        icon: isRouteActive('/supplier-notifications') ? <SvgMenuSn className={'text-content-brand'} /> : <SvgMenuSn />,
        label: tr('Layout.supplierNotification', 'SN'),
        onClick: () => nav.navigate(nav.urlFunctions.createSupplierNotificationList()),
        show:
          !hasPermission([PermissionTypesEnum.RESTRICT_NO_SUPPNOTIFICATION]) &&
          hasPermission([PermissionTypesEnum.PERMISSION_SN_LIST_READ, PermissionTypesEnum.PERMISSION_SN_LIST_RW]),
        isActive: isRouteActive('/supplier-notifications'),
      });
    }

    // Menu button (always shown)
    items.push({
      icon: (
        <div className="flex items-center justify-center h-6">
          <span className={`pi ${mobileMenuOpen ? 'pi-times' : 'pi-bars'} text-2xl`}></span>
        </div>
      ),
      label: tr('Layout.menu', 'Menu'),
      onClick: () => setMobileMenuOpen(!mobileMenuOpen),
      show: true,
    });

    return items;
  };

  // The page-layout tiles are part of the split-view beta; without it there is only the list layout.
  // Hooks, so above the early return below.
  const { version: splitViewVersion } = useBetaVersion(BetaSurface.SPLIT_VIEW);
  const splitViewEnabled = splitViewVersion === DefaultVersionEnum.NEW;

  if (oneOfPermission?.length > 0 && isNullOrUndefined(loggedUser)) return <>Loading...</>;
  const showMessagedFromOwners =
    loggedUser?.modules?.filter(t => t.inheritingModule === true).length > 0 || loggedUser?.modules?.some(t => t.companyModule === CompanyModuleEnum.CARRIER);

  const showNotificationAdmin =
    loggedUser?.modules?.filter(t => t.inheritingModule === false).length > 0 && !loggedUser?.modules?.some(t => t.companyModule === CompanyModuleEnum.CARRIER);

  if (oneOfPermission?.length > 0 && !hasPermission(oneOfPermission))
    return (
      <>
        <PermissionError permission={oneOfPermission} />
      </>
    );

  const appMenu: MenuItemModel[] = [
    {
      id: 'dashboard',
      title: tr('Layout.dashboard', 'Dashboard'),
      dataTestId: 'menuDashboard',
      icon: <SvgMenuDashboard />,
      url: nav.createNavigationLink(nav.urlFunctions.createHomePage()),
    },
    {
      id: 'expeditions-carrier',
      title: tr('Layout.expeditions', 'Expeditions'),
      dataTestId: 'menuExpeditionsCarrier',
      icon: <SvgMenuTms />,
      secured: { id: 'menu-expeditions-carrier', label: 'Expeditions (carrier)', description: "Main menu: the carrier's shipments, demands and multi-demands", visible: expeditionsCarrierFlag },
      subMenu: [
        {
          title: tr('Layout.allShipments', 'Všechny přiřazené'),
          dataTestId: 'menuExpeditionsCarrier-list',
          url: nav.createNavigationLink(nav.urlFunctions.createCarrierShipmentsList()),
        },
        {
          title: tr('Layout.allDemands', 'Seznam všech'),
          dataTestId: 'menuExpeditionsCarrier-singleDemands',
          url: nav.createNavigationLink(nav.urlFunctions.createCarrierDemandsList()),
        },
        {
          title: tr('Layout.allMultiDemands', 'Multi demands'),
          dataTestId: 'menuExpeditionsCarrier-groupDemands',
          url: nav.createNavigationLink(nav.urlFunctions.createCarrierMultiDemandsList()),
        },
      ],
    },
    {
      id: 'expeditions',
      title: tr('Layout.expeditions', 'Expeditions'),
      dataTestId: 'menuExpeditions',
      icon: <SvgMenuTms />,
      secured: { id: 'menu-expeditions', label: 'Expeditions (customer)', description: "Main menu: the customer's shipments", visible: expeditionsCustomerFlag },
      subMenu: [
        {
          title: tr('Layout.new', 'New'),
          dataTestId: 'menuExpeditions-create',
          create: true,
          url: nav.createNavigationLink(nav.urlFunctions.createExpedition()),
          secured: { id: 'menu-expeditions-create', label: 'New', description: 'Main menu: create a new shipment', visible: expeditionCreateFlag },
        },
        {
          title: tr('Layout.expeditionList', 'Expedition list'),
          dataTestId: 'menuExpeditions-list',
          url: nav.createNavigationLink(nav.urlFunctions.createCustomerExpeditionList()),
          secured: { id: 'menu-expeditions-list', label: 'Expedition list', description: 'Main menu: list of shipments', visible: expeditionListFlag },
        },
        {
          title: tr('Layout.routes', 'Templates'),
          dataTestId: 'menuExpeditions-templates',
          url: nav.createNavigationLink(nav.urlFunctions.createCustomerExpeditionTemplates()),
          secured: { id: 'menu-expeditions-templates', label: 'Templates', description: 'Main menu: shipment templates', visible: expeditionTemplatesFlag },
        },
      ],
    },
    {
      id: 'timeslots',
      title: tr('Layout.timeslots', 'Timeslots'),
      dataTestId: 'menuTimeslots',
      icon: <SvgMenuTimeslot />,
      secured: { id: 'menu-timeslots', label: 'Timeslots', description: 'Main menu: timeslots and reservations', visible: timeslotsFlag },
      subMenu: [
        {
          title: tr('Layout.new', 'New'),
          dataTestId: 'menuTimeslots-create',
          create: true,
          // No url: this opens the create sidebar in place, so a reservation can be booked from
          // wherever the user happens to be.
          onClick: () =>
            setCreateTimeslotRequest({
              applicationId: null,
              companyLocationId: null,
              preferredDate: '',
              companyLocationCustomerId: null,
              expeditionLocationId: null,
              expeditionId: null,
              timezoneId: null,
            }),
          secured: { id: 'menu-timeslots-new', label: 'New', description: 'Main menu: create a new reservation', visible: timeslotCreateFlag },
        },
        {
          title: tr('Layout.timeSlotsDashboard', 'Calendar'),
          dataTestId: 'menuTimeslots-calendar',
          url: nav.createNavigationLink(nav.urlFunctions.createTimeslotCalendar()),
          secured: { id: 'menu-timeslots-calendar', label: 'Calendar', description: 'Main menu: timeslot calendar of the ramps', visible: timeslotsCalendarFlag },
        },
        {
          title: tr('Layout.reservation', 'Reservation list'),
          dataTestId: 'menuTimeslots-reservationList',
          url: nav.createNavigationLink(nav.urlFunctions.createReservationList()),
          secured: { id: 'menu-timeslots-reservation-list', label: 'Reservation list', description: 'Main menu: list of reservations', visible: timeslotsReservationListFlag },
        },
        {
          title: tr('Layout.cams', 'Cams'),
          url: nav.createNavigationLink(nav.urlFunctions.createCamsDetail()),
          secured: { id: 'menu-timeslots-cams', label: 'Cams', description: 'Main menu: entry camera log', visible: camsFlag },
        },
        {
          title: tr('Layout.camsSettings', 'Cams settings'),
          url: nav.createNavigationLink(nav.urlFunctions.createCamsSettings()),
          secured: { id: 'menu-timeslots-cams-settings', label: 'Cams settings', description: 'Main menu: entry camera settings', visible: camsSettingsFlag },
        },
        {
          title: tr('Layout.areaVisiting', 'Visitors'),
          url: nav.createNavigationLink(nav.urlFunctions.createVisitorList()),
          secured: { id: 'menu-timeslots-visitors', label: 'Visitors', description: 'Main menu: visitor records', visible: visitorsFlag },
        },
      ],
    },
    {
      id: 'supplier-notifications',
      title: tr('Layout.supplierNotification', 'Supplier notifications'),
      icon: <SvgMenuSn />,
      dataTestId: 'menuSupplierNotifications',
      secured: { id: 'menu-supplier-notifications', label: 'Supplier notifications', description: 'Main menu: supplier notifications', visible: supplierNotificationFlag },
      subMenu: [
        {
          title: tr('Layout.new', 'New'),
          url: nav.createNavigationLink(nav.urlFunctions.createSupplierNotificationCreate()),
          secured: { id: 'menu-sn-new', label: 'New', description: 'Main menu: create a new supplier notification', visible: snNewFlag },
          dataTestId: 'menuSupplierNotifications-new',
          create: true,
        },
        {
          title: tr('Layout.newDap', 'Nová DAP'),
          url: nav.createNavigationLink(nav.urlFunctions.createSupplierNotificationDapCreate()),
          secured: { id: 'menu-sn-new-dap', label: 'New DAP', description: 'Main menu: create a new DXX (DAP) supplier notification', visible: snNewDapFlag },
          dataTestId: 'menuSupplierNotifications-newDap',
          create: true,
        },
        {
          title: tr('Layout.listSupplierNotifications', 'Supplier notification list'),
          url: nav.createNavigationLink(nav.urlFunctions.createSupplierNotificationList()),
          secured: { id: 'menu-sn-list', label: 'Supplier notification list', description: 'Main menu: list of supplier notifications', visible: snListFlag },
          dataTestId: 'menuSupplierNotifications-list',
        },
        {
          title: tr('Layout.groupDemands', 'Group demands'),
          url: nav.createNavigationLink(nav.urlFunctions.createMultiDemansList()),
          secured: { id: 'menu-sn-group-demands', label: 'Group demands', description: 'Main menu: group demands from supplier notifications', visible: snGroupDemandsFlag },
          dataTestId: 'menuSupplierNotifications-groupDemands',
        },
        {
          title: tr('Layout.shipmentsNew', 'Agent notification'),
          url: nav.createNavigationLink(nav.urlFunctions.createShipmentList()),
          secured: { id: 'menu-sn-shipments', label: 'Agent notification', description: 'Main menu: agent notifications (shipments)', visible: snShipmentsFlag },
          dataTestId: 'menuSupplierNotifications-shipmentsNew',
        },
      ],
    },
    {
      id: 'reports',
      title: 'Reporty',
      dataTestId: 'menuReports',
      icon: <SvgMenuReports />,
      secured: { id: 'menu-reports', label: 'Reports', description: 'Main menu: reports assigned to the user', visible: reportsFlag },
      subMenu: loggedUser?.reports?.map(t => ({
        title: t.reportName,
        url: nav.createNavigationLink(nav.urlFunctions.createReportUrl(t.reportId)),
      })),
    },
    {
      id: 'contacts',
      title: tr('Layout.contacts', 'Contacts'),
      dataTestId: 'menuContacts',
      icon: <SvgMenuContacts />,
      secured: { id: 'menu-contacts', label: 'Contacts', description: 'Main menu: carriers and partners', visible: contactsFlag },
      subMenu: [
        {
          title: tr('Layout.myCarriers', 'My carriers'),
          url: nav.createNavigationLink(nav.urlFunctions.createMyCarrierList()),
          secured: { id: 'menu-contacts-my-carriers', label: 'My carriers', description: "Main menu: the company's carriers", visible: myCarriersFlag },
        },
        {
          title: tr('Layout.carrierGroups', 'Carrier groups'),
          url: nav.createNavigationLink(nav.urlFunctions.createCarrierGroups()),
          secured: { id: 'menu-contacts-carrier-groups', label: 'Carrier groups', description: 'Main menu: carrier groups', visible: carrierGroupsFlag },
        },
        {
          title: tr('Layout.carrierCatalog', 'Catalog'),
          url: nav.createNavigationLink(nav.urlFunctions.createCarrierCatalogList()),
          secured: { id: 'menu-contacts-carrier-catalog', label: 'Catalog', description: 'Main menu: catalog of carriers', visible: carrierCatalogFlag },
        },
        {
          title: tr('Layout.partners', 'Partners'),
          url: nav.createNavigationLink(nav.urlFunctions.createPartnerList()),
          secured: { id: 'menu-contacts-partners', label: 'Partners', description: "Main menu: the company's partners", visible: contactPartnersFlag },
        },
      ],
    },
    {
      id: 'activities',
      title: tr('Layout.activities', 'Aktivity'),
      icon: <SvgMenuActivities />,
      dataTestId: 'menuActivities',
      secured: { id: 'menu-activities', label: 'Activities', description: 'Main menu: activities (communication history, jobs, AI dispatcher activity)', visible: activitiesFlag },
      elementCount: { errorCount: jobBadges.errorCount, warningCount: jobBadges.doneWithErrorCount },
      subMenu: [
        {
          title: tr('Layout.jobs', 'Úkoly'),
          url: nav.createNavigationLink(nav.urlFunctions.createJobList()),
          secured: { id: 'menu-jobs', label: 'Jobs center', description: 'Main menu: jobs center: background tasks and their files to download', visible: jobsFlag },
          elementCount: { errorCount: jobBadges.errorCount, warningCount: jobBadges.doneWithErrorCount },
          dataTestId: 'menuActivities-jobs',
        },
        {
          title: tr('EmailHistory.pageTitle', 'Communication history'),
          url: nav.createNavigationLink(nav.urlFunctions.createEmailHistoryList()),
          secured: { id: 'menu-activities-email-history', label: 'Communication history', description: 'Main menu: history of e-mails and messages sent', visible: emailHistoryFlag },
          dataTestId: 'menuActivities-emailHistory',
        },
        {
          title: tr('AiDispatcher.activityPageTitle', 'AI dispatcher activity'),
          url: nav.createNavigationLink(nav.urlFunctions.aiDispatcherActivityUrlInfo()),
          secured: { id: 'menu-activities-ai-dispatcher', label: 'AI dispatcher activity', description: 'Main menu: what the AI dispatcher did', visible: aiDispatcherActivityFlag },
          dataTestId: 'menuActivities-aiDispatcher',
        },
      ],
    },
    {
      id: 'settings',
      title: tr('Layout.settings', 'Settings'),
      icon: <SvgMenuSettings />,
      dataTestId: 'menuSettings',
      secured: { id: 'menu-settings', label: 'Settings', description: 'Main menu: company settings', visible: settingsFlag },
      subMenu: [
        {
          title: tr('Layout.locations', 'Lokality'),
          url: nav.createNavigationLink(nav.urlFunctions.createLocationsList()),
          secured: { id: 'menu-settings-locations', label: 'Locations', description: "Main menu: the company's locations and ramps", visible: locationsFlag },
        },
        {
          title: tr('AppTopbar.companyProfile', 'Company profile'),
          url: nav.createNavigationLink(nav.urlFunctions.createCompanyProfile()),
          secured: { id: 'menu-settings-company-profile', label: 'Company profile', description: 'Main menu: the company profile', visible: companyProfileFlag },
        },
        {
          title: tr('AppTopbar.inheritedCompanies', 'Inherited companies'),
          url: nav.createNavigationLink(nav.urlFunctions.createIhneritedCompanies()),
          secured: { id: 'menu-settings-inherited-companies', label: 'Inherited companies', description: 'Main menu: inherited companies', visible: inheritedCompaniesFlag },
        },
        {
          title: tr('AppTopbar.partners', 'Partners'),
          url: nav.createNavigationLink(nav.urlFunctions.partnersUrlInfo()),
          secured: { id: 'menu-settings-partners', label: 'Partners', description: 'Main menu: partner settings', visible: settingsPartnersFlag },
        },
        {
          title: tr('AppTopbar.companyNotifications', 'Námi rozeslaná upozornění'),
          url: nav.createNavigationLink(nav.urlFunctions.createCompanyNotificationList()),
          secured: { id: 'menu-settings-company-notifications', label: 'Company notifications', description: 'Main menu: company notification settings', visible: companyNotificationsFlag },
        },
        {
          title: tr('Layout.exceptions', 'Exceptions'),
          url: nav.createNavigationLink(nav.urlFunctions.createExceptionList()),
          secured: { id: 'menu-settings-exceptions', label: 'Exceptions', description: 'Main menu: exceptions: periods when locations or ramps are closed', visible: exceptionsFlag },
        },
        {
          title: tr('AppTopbar.volumetric', 'Volumetric'),
          url: nav.createNavigationLink(nav.urlFunctions.volumetricUrlInfo()),
          secured: { id: 'menu-settings-volumetric', label: 'Volumetric', description: 'Main menu: volumetric settings (items with their external id and name)', visible: volumetricFlag },
        },
        {
          title: tr('AppTopbar.calendarLayout', 'Calendar card layouts'),
          url: nav.createNavigationLink(nav.urlFunctions.calendarLayoutUrlInfo()),
          secured: { id: 'menu-settings-calendar-layout', label: 'Calendar card layouts', description: 'Main menu: layouts of the timeslot calendar cards', visible: calendarLayoutFlag },
        },
        {
          title: tr('AppTopbar.aiDispatcher', 'AI dispatcher rules'),
          url: nav.createNavigationLink(nav.urlFunctions.aiDispatcherUrlInfo()),
          secured: { id: 'menu-settings-ai-dispatcher', label: 'AI dispatcher rules', description: 'Main menu: AI dispatcher rules', visible: aiDispatcherFlag },
        },
      ],
    },
  ] as MenuItemModel[];

  const userMenu = [
    languageMenuItem,
    {
      title: (
        <div>
          <div data-testid="loginUserName">
            {loggedUser?.firstName} {loggedUser?.lastName}
          </div>
          <Note data-testid="loginUserCompanyName">{loggedUser?.company}</Note>
        </div>
      ),
      icon: <SvgMenuUser />,
      mode: 'open-outside-bottom',
      subMenu: [
        // Beta SPLIT_VIEW: the page layout (list / side by side / stacked), honoured by the
        // pages that support a split.
        ...(splitViewEnabled ? [{ title: '', content: <PageLayoutTiles />, dataTestId: 'menu-page-layout' }] : []),
        {
          title: tr('Layout.profile', 'Profile'),
          url: nav.createNavigationLink(nav.urlFunctions.createMyProfile()),
        },
        {
          title: tr('Layout.logout', 'Logout'),
          onClick: () => logout({ logoutParams: { returnTo: window.location.origin } }),
        },
      ],
    },
  ] as MenuItemModel[];

  if (isNullOrUndefined(loggedUser)) <></>;

  return (
    <>
      {title && <title>{title}</title>}
      <div className="flex flex-col h-screen overflow-hidden">
        <MaintenanceBanner />
        {createTimeslotRequest && (
          <CreateTimeslotSidebar
            headerDisabled={false}
            onContinue={reservation => {
              if (reservation?.id) nav.navigate(nav.urlFunctions.createReservationDetail(reservation.id));
            }}
          />
        )}
        <div className={'flex flex-col md:flex-row bg-base-tertiary flex-1 min-h-0 overflow-hidden'}>
          {isMobile && (
            <div className="fixed bottom-0 left-0 right-0 bg-white z-20 py-3 px-4 border-t flex justify-between items-center shadow-lg">
              {getMobileMenuItems().map(
                (item, index) =>
                  item.show && (
                    <button
                      key={index}
                      className={`flex flex-col items-center justify-center text-xs w-20 border-none bg-transparent ${
                        item.isActive ? 'text-[#FE5000]' : 'text-gray-600'
                      }`}
                      onClick={item.onClick}
                    >
                      {item.icon}
                      <span className="mt-1">{item.label}</span>
                    </button>
                  ),
              )}
            </div>
          )}

          <AppSidebar
            state={sidebar}
            menu={appMenu}
            userMenu={userMenu}
            menuStorageKey={'Layout.menuOpen1'}
            userMenuStorageKey={'Layout.menuOpen2'}
            panelClassName={clsx({ 'bg-[var(--ringil-error-color)] text-white': runAs && isProduction() })}
            mobileOpen={mobileMenuOpen}
            onMobileClose={() => setMobileMenuOpen(false)}
            bottomSlot={({ smallMenu }) => (
              <>
                <ul>
                  <NotificationMenuItem smallMenu={smallMenu} />
                  {showMessagedFromOwners === true && <CustomerNotificationMenuItem smallMenu={smallMenu} onClick={() => setVisible(true)} />}
                  {/* Neither a link nor a group: the entry opens the assistant panel, which is mounted
                    by AssistantProvider. It sits above the divider, between the notifications and the
                    language / user entries, as in the menu's Bottom section in Figma. */}
                  <MenuItem
                    smallMenu={smallMenu}
                    title={tr('Layout.aiAssistant', 'Poradím vám')}
                    icon={<SvgAiAssistant className={'w-5 h-5'} />}
                    dataTestId={'menuAiAssistant'}
                    secured={{ id: 'menu-ai-assistant', label: 'AI assistant', description: 'Main menu: opens the AI assistant', visible: assistantFlag }}
                    attention={true}
                    onClick={() => openAssistant()}
                  />
                </ul>
                <hr className={'bg-base-base h-[1px] border-0'} />
                {(isAdmin() || !!runAs) && <SecurityModeMenuSwitch smallMenu={smallMenu} />}
                <Customer2InheritorNotificationSidebar setVisible={v => setVisible(v)} visible={visible} />
              </>
            )}
          />
          {/* The row is menu | content | rest. With the improved layout on, the content is capped and
            sits against the menu; the leftover width is the rest region, which is where a panel goes
            when it fits and where the watermark lives otherwise. With it off the content takes the
            whole row and there is no rest region. */}
          <main className={clsx('relative flex-1 flex w-full min-w-0 overflow-hidden', { 'pb-16': isMobile })}>
            {!isMobile && improvedLayout && <AppWatermark restWidth={restWidth} />}
            <div
              ref={contentRef}
              className={clsx('relative z-[1] flex flex-col flex-1 min-w-0 overflow-auto', {
                // The right gutter is padding rather than margin so the scrollbar renders inside the
                // gutter instead of on top of the content; the cap grows by the same amount to keep
                // the content itself capped at 1550px.
                // The cap only applies with the improved layout on — see improvedLayoutStore.
                'ml-[var(--app-gutter,24px)] pr-[var(--app-gutter,24px)] [scrollbar-gutter:stable]': !isMobile,
                'max-w-[calc(1550px+var(--app-gutter,24px))]': !isMobile && improvedLayout,
                'max-w-[1550px]': isMobile && improvedLayout,
              })}
              {...{ [CONTENT_SCROLL_ATTR]: true }}
            >
              <div className={clsx('flex flex-col flex-1', layout === 'detail' ? 'px-4 md:mx-2 md:pb-10' : 'pl-0.5 pb-0.5 pr-0.5 gap-2')}>
                {isMobile && mobileMenuOpen && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 z-20 transition-opacity duration-150" onClick={() => setMobileMenuOpen(false)}></div>
                )}
                {children}
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default ResponsiveLayout;
